import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { getLeaderboard, getLiveScores, updateTeamScore } from '../actions/leaderboardActions'
import PlayerImageBadge from '../components/PlayerImageBadge'

// ── Country name/code → ISO 2-letter code ────────────────────────────────────
// Returns a lowercase 2-letter ISO code suitable for flagcdn.com, or null.
const COUNTRY_CODES = {
    'united states': 'us', 'usa': 'us', 'u.s.a.': 'us', 'u.s.': 'us',
    'canada': 'ca', 'mexico': 'mx', 'united kingdom': 'gb', 'uk': 'gb', 'england': 'gb',
    'france': 'fr', 'germany': 'de', 'spain': 'es', 'italy': 'it',
    'brazil': 'br', 'argentina': 'ar', 'colombia': 'co', 'chile': 'cl',
    'australia': 'au', 'new zealand': 'nz', 'japan': 'jp', 'china': 'cn',
    'south korea': 'kr', 'korea': 'kr', 'india': 'in', 'nigeria': 'ng',
    'ghana': 'gh', 'south africa': 'za', 'kenya': 'ke', 'jamaica': 'jm',
    'trinidad': 'tt', 'trinidad and tobago': 'tt', 'bahamas': 'bs',
    'dominican republic': 'do', 'puerto rico': 'pr', 'cuba': 'cu',
    'haiti': 'ht', 'panama': 'pa', 'costa rica': 'cr',
}

// Returns lowercase ISO code or null
function countryFlag(country) {
    if (!country) return null
    const lower = country.trim().toLowerCase()
    return lower.length === 2 ? lower : (COUNTRY_CODES[lower] || null)
}

// Returns the flagcdn.com SVG URL for a country value, or null
function flagSvgUrl(country) {
    const code = countryFlag(country)
    return code ? `https://flagcdn.com/${code}.svg` : null
}

// ── Medal colours ──────────────────────────────────────────────────────────────
const GOLD   = { bg: 'linear-gradient(135deg,#c8a951 0%,#f0d080 50%,#c8a951 100%)', text: '#5a3d00', border: '#c8a951' }
const SILVER = { bg: 'linear-gradient(135deg,#a0aab4 0%,#d4dce4 50%,#a0aab4 100%)', text: '#2e3a45', border: '#a0aab4' }
const BRONZE = { bg: 'linear-gradient(135deg,#9a6644 0%,#c8895c 50%,#9a6644 100%)', text: '#3d1a00', border: '#9a6644' }
const PLAIN  = { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' }

function medalStyle(rank) {
    if (rank === 1) return GOLD
    if (rank === 2) return SILVER
    if (rank === 3) return BRONZE
    return PLAIN
}

function rankLabel(rank) {
    if (rank === 1) return '🥇'
    if (rank === 2) return '🥈'
    if (rank === 3) return '🥉'
    return `#${rank}`
}

function Avatar({ player, size = 48 }) {
    const flag     = countryFlag(player.country)
    const initials = player.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    const badgeSize = Math.round(size * 0.42)

    return (
        <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>

            {/* Profile image or initials circle */}
            {player.image ? (
                <img
                    src={player.image}
                    alt={player.name}
                    style={{
                        width: size, height: size, borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2.5px solid var(--bnb-navy)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        display: 'block',
                    }}
                />
            ) : (
                <div style={{
                    width: size, height: size, borderRadius: '50%',
                    background: 'var(--bnb-navy)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: size * 0.35, fontWeight: '800',
                    color: '#fff',
                    border: '2.5px solid var(--bnb-navy)',
                    letterSpacing: '-0.02em',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
                }}>
                    {initials}
                </div>
            )}

            {/* Country flag SVG badge — bottom-right overlay */}
            {flag && (
                <div style={{
                    position: 'absolute',
                    bottom: -2,
                    right: -3,
                    width: badgeSize,
                    height: badgeSize,
                    borderRadius: '50%',
                    boxShadow: '0 1px 5px rgba(0,0,0,0.28)',
                    border: '1.5px solid rgba(255,255,255,0.95)',
                    zIndex: 2,
                    overflow: 'hidden',
                    background: '#e5e7eb',
                }}>
                    <img
                        src={flagSvgUrl(player.country)}
                        alt={player.country}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                </div>
            )}
        </div>
    )
}

function StatPill({ icon, value, label, accent }) {
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            minWidth: 64, padding: '0.35rem 0.6rem',
            background: 'rgba(0,0,0,0.06)', borderRadius: 8,
        }}>
            <span style={{ fontSize: '0.65rem', color: accent || 'rgba(0,0,0,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 2 }}>
                {icon} {label}
            </span>
            <span style={{ fontSize: '1.1rem', fontWeight: 900, color: accent || '#1a2035', lineHeight: 1 }}>
                {value}
            </span>
        </div>
    )
}

// ── Trophy badge with hover tooltip ─────────────────────────────────────────
function TrophyBadge({ player }) {
    const [hovered, setHovered] = useState(false)
    const count = player.tournament_wins
    if (count === 0) return null

    return (
        <div
            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {/* Badge */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: '0.25rem',
                background: 'linear-gradient(135deg, #c8a951 0%, #f0d080 50%, #c8a951 100%)',
                borderRadius: 8, padding: '0.3rem 0.6rem',
                boxShadow: '0 2px 8px rgba(200,169,81,0.45)',
                cursor: 'default',
            }}>
                <span style={{ fontSize: '1.15rem', lineHeight: 1 }}>🏆</span>
                <span style={{ fontWeight: 900, fontSize: '0.9rem', color: '#5a3d00', letterSpacing: '0.02em' }}>×{count}</span>
            </div>

            {/* Hover tooltip — opens to the left */}
            {hovered && player.winning_tournaments && player.winning_tournaments.length > 0 && (
                <div style={{
                    position: 'absolute', right: 'calc(100% + 0.5rem)', top: '50%', transform: 'translateY(-50%)',
                    background: '#fff',
                    border: '1.5px solid var(--bnb-navy)',
                    borderRadius: 10,
                    padding: '0.55rem 0.8rem',
                    minWidth: 190,
                    boxShadow: '0 6px 24px rgba(0,0,0,0.14)',
                    zIndex: 100,
                    whiteSpace: 'nowrap',
                }}>
                    <div style={{ fontSize: '0.58rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--bnb-navy)', marginBottom: '0.45rem' }}>
                        Tournament Wins
                    </div>
                    {player.winning_tournaments.map((t, i) => (
                        <div key={i} style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            paddingBottom: i < player.winning_tournaments.length - 1 ? '0.3rem' : 0,
                            marginBottom: i < player.winning_tournaments.length - 1 ? '0.3rem' : 0,
                            borderBottom: i < player.winning_tournaments.length - 1 ? '1px solid #f3f4f6' : 'none',
                        }}>
                            {t.logo_url
                                ? <img src={t.logo_url} alt="" style={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }} />
                                : <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>⛳</span>
                            }
                            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1f2937' }}>{t.name || t}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

function PlayerCard({ player, index }) {
    const ms = PLAIN
    const isTop3 = player.rank <= 3

    return (
        <div
            style={{
                overflow: 'auto',
                display: 'flex', alignItems: 'center', gap: '1rem',
                background: ms.bg,
                border: `1.5px solid ${ms.border}`,
                borderRadius: 12,
                padding: isTop3 ? '1.1rem 1.25rem' : '0.8rem 1.1rem',
                marginBottom: '0.65rem',
                boxShadow: isTop3
                    ? `0 4px 18px rgba(0,0,0,0.10), 0 0 0 1px ${ms.border}44`
                    : '0 1px 4px rgba(0,0,0,0.06)',
                animation: `bnb-fadeUp 0.4s ease ${(index * 0.05).toFixed(2)}s both`,
                position: 'relative',
            }}
        >
            {/* Subtle shine on top-3 cards */}
            {isTop3 && (
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: '40%',
                    background: 'linear-gradient(180deg,rgba(255,255,255,0.18) 0%,transparent 100%)',
                    pointerEvents: 'none',
                }} />
            )}

            {/* Avatar */}
            <Avatar player={player} size={isTop3 ? 79 : 67} />

            {/* Name + location */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontWeight: 800, fontSize: isTop3 ? '1.05rem' : '0.95rem',
                    color: ms.text, lineHeight: 1.2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                    {player.name}
                </div>
                {/* Birth place (country shown as flag badge on avatar) */}
                {(player.birth_place || player.country) && (
                    <div style={{
                        fontSize: '0.72rem', color: isTop3 ? `${ms.text}99` : '#9ca3af',
                        fontWeight: 500, marginTop: 2,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                        {[player.birth_place, !countryFlag(player.country) ? player.country : null]
                            .filter(Boolean).join(', ')}
                    </div>
                )}
                {/* Winning tournaments tooltip area — replaced by TrophyBadge */}
            </div>

            {/* Trophy badge with hover tooltip */}
            <TrophyBadge player={player} />

            {/* Stats */}
            <div style={{ display: 'flex', gap: '0.45rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {player.contest_wins > 0 && (
                    <StatPill
                        icon="🎯"
                        label="Contests"
                        value={player.contest_wins}
                        accent={isTop3 ? ms.text : undefined}
                    />
                )}
                {player.award_wins > 0 && (
                    <StatPill
                        icon="🎖️"
                        label="Awards"
                        value={player.award_wins}
                        accent={isTop3 ? ms.text : undefined}
                    />
                )}
                <StatPill
                    icon="⛳"
                    label="Played"
                    value={player.tournaments_played}
                    accent={isTop3 ? ms.text : undefined}
                />
            </div>
        </div>
    )
}

// ── Live scoreboard section ────────────────────────────────────────────────────
function LiveSection({ currentUserId }) {
    const dispatch  = useDispatch()
    const navigate  = useNavigate()
    const { tournaments = [], loading } = useSelector(state => state.liveScores)
    const [editingTeam, setEditingTeam] = useState(null)
    const [editScore,   setEditScore]   = useState('')
    const [saving,      setSaving]      = useState(false)
    const [saveError,   setSaveError]   = useState(null)

    const refresh = useCallback(() => { dispatch(getLiveScores()) }, [dispatch])

    // Initial fetch on mount
    useEffect(() => { refresh() }, [refresh])

    // Only poll every 30s if there are actually live tournaments
    useEffect(() => {
        if (loading || tournaments.length === 0) return
        const timer = setInterval(refresh, 30000)
        return () => clearInterval(timer)
    }, [refresh, loading, tournaments.length])

    const startEdit = (team) => {
        setEditingTeam(team.id)
        setEditScore(team.score !== null ? String(team.score) : '')
        setSaveError(null)
    }

    const cancelEdit = () => { setEditingTeam(null); setEditScore(''); setSaveError(null) }

    const handleSave = async (teamId) => {
        const parsed = parseFloat(editScore)
        if (isNaN(parsed)) { setSaveError('Enter a valid number'); return }
        setSaving(true); setSaveError(null)
        try {
            await dispatch(updateTeamScore(teamId, parsed))
            setEditingTeam(null); setEditScore('')
        } catch {
            setSaveError('Failed to save — try again')
        } finally {
            setSaving(false)
        }
    }

    if (!loading && tournaments.length === 0) return null

    // Determine section label based on round dates
    const getRoundLabel = () => {
        const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD format
        
        let hasToday = false
        let hasUpcoming = false
        let hasLatest = false
        
        tournaments.forEach(t => {
            const roundDate = t.day.day_date
            if (roundDate === today) hasToday = true
            else if (roundDate > today) hasUpcoming = true
            else hasLatest = true
        })
        
        if (hasToday) return "Today's Round"
        if (hasUpcoming) return 'Upcoming Round'
        return 'Latest Round'
    }

    return (
        <div style={{ marginBottom: '2rem' }}>

            {/* Section header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '1rem' }}>
                <span style={{
                    background: '#dc2626', color: '#fff',
                    fontSize: '0.62rem', fontWeight: 900,
                    padding: '0.18rem 0.5rem', borderRadius: 4,
                    letterSpacing: '0.12em',
                    animation: 'livePulse 1.5s ease-in-out infinite',
                }}>● LIVE</span>
                <span style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--bnb-navy)' }}>
                    {getRoundLabel()}
                </span>
                <span style={{ fontSize: '0.7rem', color: '#9ca3af', marginLeft: 'auto' }}>
                    Updates every 30s
                </span>
            </div>

            {loading && tournaments.length === 0 && (
                <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                    <Spinner animation="border" size="sm" style={{ color: 'var(--bnb-navy)' }} />
                </div>
            )}

            {tournaments.map(t => (
                <div key={t.id} style={{
                    background: '#fff',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: 14,
                    overflow: 'hidden',
                    marginBottom: '1rem',
                    boxShadow: '0 2px 14px rgba(0,0,0,0.08)',
                }}>

                    {/* Tournament header bar */}
                    <div style={{
                        background: 'var(--bnb-navy)',
                        padding: '0.7rem 1.25rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                        <div style={{ cursor: 'pointer' }} onClick={() => navigate(`/events/${t.id}`)}>
                            <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem', transition: 'opacity 0.2s' }}
                                 onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                                 onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                                {t.name}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', marginTop: 1 }}>
                                Day {t.day.order} · {t.day.course_name}
                                {t.day.start_time && ` · ${t.day.start_time}`}
                            </div>
                        </div>
                        <span style={{
                            background: '#dc2626', color: '#fff',
                            fontSize: '0.6rem', fontWeight: 900,
                            padding: '0.18rem 0.5rem', borderRadius: 4,
                            letterSpacing: '0.1em',
                            animation: 'livePulse 1.5s ease-in-out infinite',
                        }}>● LIVE</span>
                    </div>

                    {/* Team rows */}
                    {t.teams.length === 0 ? (
                        <div style={{ padding: '1.25rem', color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center' }}>
                            No teams assigned yet.
                        </div>
                    ) : (
                        <>
                            {/* Column headers */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '52px 1fr auto',
                                padding: '0.4rem 1.25rem',
                                background: '#f1f5f9',
                                borderBottom: '1px solid #e5e7eb',
                                fontSize: '0.63rem', fontWeight: 900,
                                textTransform: 'uppercase', letterSpacing: '0.1em',
                                color: '#94a3b8',
                            }}>
                                <div>Rank</div>
                                <div>Team</div>
                                <div style={{ textAlign: 'right' }}>Score</div>
                            </div>

                            {t.teams.slice(0, 10).map((team, idx) => {
                                const isMyTeam = Boolean(currentUserId && team.members.some(m => m.id === currentUserId))
                                const isEditing = editingTeam === team.id
                                const scored = team.score !== null
                                const pos = scored ? idx + 1 : null
                                const rankColors = ['#c8a951', '#94a3b8', '#b87333']

                                return (
                                    <div key={team.id} style={{
                                        display: 'grid',
                                        gridTemplateColumns: '52px 1fr auto',
                                        alignItems: 'center',
                                        padding: '0.7rem 1.25rem',
                                        borderBottom: idx < Math.min(t.teams.length, 10) - 1 ? '1px solid #f3f4f6' : 'none',
                                        background: isMyTeam ? 'rgba(0,48,135,0.04)' : idx === 0 && scored ? 'rgba(200,169,81,0.06)' : 'transparent',
                                        transition: 'background 0.2s',
                                    }}>

                                        {/* Rank */}
                                        <div style={{
                                            fontWeight: 900, fontSize: '1.1rem', lineHeight: 1,
                                            color: pos && pos <= 3 ? rankColors[pos - 1] : '#d1d5db',
                                        }}>
                                            {pos === 1 ? '🥇' : pos === 2 ? '🥈' : pos === 3 ? '🥉' : pos ? `#${pos}` : '—'}
                                        </div>

                                        {/* Team name + member avatars */}
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <div style={{ display: 'flex', gap: '0.2rem' }}>
                                                    {team.members.map(m => <PlayerImageBadge key={m.id} imageUrl={m.image} playerName={m.name} country={m.country} size={36} badgeSize={18} />)}
                                                </div>
                                                <span style={{
                                                    fontWeight: 800, fontSize: '0.92rem',
                                                    color: isMyTeam ? 'var(--bnb-navy)' : '#111827',
                                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                }}>{team.name}</span>
                                                {isMyTeam && (
                                                    <span style={{
                                                        background: 'var(--bnb-navy)', color: '#fff',
                                                        fontSize: '0.5rem', fontWeight: 800,
                                                        padding: '0.1rem 0.32rem', borderRadius: 3,
                                                        letterSpacing: '0.06em', flexShrink: 0,
                                                    }}>YOU</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Score / inline edit */}
                                        {isEditing ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                <input
                                                    type="number"
                                                    value={editScore}
                                                    onChange={e => setEditScore(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Enter') handleSave(team.id); if (e.key === 'Escape') cancelEdit() }}
                                                    autoFocus
                                                    style={{
                                                        width: 68, padding: '0.3rem 0.45rem',
                                                        border: '1.5px solid var(--bnb-navy)',
                                                        borderRadius: 6, fontSize: '0.85rem',
                                                        fontWeight: 700, textAlign: 'center', outline: 'none',
                                                    }}
                                                />
                                                <button onClick={() => handleSave(team.id)} disabled={saving}
                                                    style={{ background: 'var(--bnb-navy)', color: '#fff', border: 'none', borderRadius: 6, padding: '0.3rem 0.65rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}
                                                >{saving ? '…' : 'Save'}</button>
                                                <button onClick={cancelEdit}
                                                    style={{ background: 'transparent', color: '#9ca3af', border: 'none', cursor: 'pointer', fontSize: '0.9rem', padding: '0.3rem' }}
                                                >✕</button>
                                                {saveError && <span style={{ fontSize: '0.68rem', color: '#dc2626' }}>{saveError}</span>}
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', justifyContent: 'flex-end' }}>
                                                <div style={{
                                                    fontWeight: 900,
                                                    fontSize: scored ? '1.6rem' : '1rem',
                                                    color: pos === 1 ? '#c8a951' : scored ? 'var(--bnb-navy)' : '#d1d5db',
                                                    lineHeight: 1, minWidth: 48, textAlign: 'right',
                                                }}>
                                                    {scored ? team.score : <span style={{ color: '#d1d5db', fontSize: '0.85rem' }}>—</span>}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </>
                    )}
                </div>
            ))}
        </div>
    )
}

const SORT_OPTIONS = [
    { key: 'tournament_wins', label: 'Winners',  icon: '🏆' },
    { key: 'contest_wins',    label: 'Contests', icon: '🎯' },
    { key: 'award_wins',      label: 'Awards',   icon: '🎖️' },
]

function LeaderboardScreen() {
    const dispatch = useDispatch()
    const { loading, error, players = [] } = useSelector(state => state.leaderboard)
    const { userInfo } = useSelector(state => state.userLogin)
    const { tournaments: liveTournaments = [] } = useSelector(state => state.liveScores)
    const currentUserId = userInfo ? userInfo.id : null
    const [sortKey, setSortKey] = useState('tournament_wins')
    const isLive = liveTournaments.length > 0

    useEffect(() => {
        dispatch(getLeaderboard())
    }, [dispatch])

    const sortedPlayers = [...players].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0))

    return (
        <div className="bnb-leaderboard-hero-top-padding" >

            {/* ── Hero ──────────────────────────────────────────────── */}
            <div className="bnb-hero mb-4" style={{ paddingBottom: 0, minHeight: '190px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {/* text */}
                <div className="bnb-hero-eyebrow">
                    <i className="fa-solid fa-trophy me-2"></i>All-Time Rankings
                </div>
                <h1 className="bnb-hero-title">Leaderboard</h1>
                <p className="bnb-hero-sub page-leaderboard">
                    Every BNB ever run — tallied up. Who's got the most wins?
                </p>

                {/* podium — absolutely pinned to bottom-right */}
                {!loading && sortedPlayers.length >= 3 && (
                    <div style={{
                        position: 'absolute', bottom: 0, right: '2rem',
                        display: 'flex', alignItems: 'flex-end', gap: '0.5rem',
                    }}>
                        <PodiumBlock player={sortedPlayers[1]} podiumHeight={55} small />
                        <PodiumBlock player={sortedPlayers[0]} podiumHeight={70} crown small />
                        <PodiumBlock player={sortedPlayers[2]} podiumHeight={40} small />
                    </div>
                )}
            </div>

            {/* ── Player list ───────────────────────────────────────── */}
            <div style={{ maxWidth: 820, margin: '0 auto' }}>

                {/* Live scorecard ─ sits above all results */}
                <LiveSection currentUserId={currentUserId} />

                {loading && (
                    <div style={{ textAlign: 'center', padding: '3rem' }}>
                        <Spinner animation="border" style={{ color: 'var(--bnb-navy)' }} />
                    </div>
                )}

                {error && (
                    <div style={{
                        background: '#fef2f2', border: '1px solid #fca5a5',
                        borderRadius: 8, padding: '1rem 1.25rem', color: '#991b1b',
                        marginBottom: '1rem',
                    }}>
                        {error}
                    </div>
                )}

                {!loading && !error && players.length === 0 && (
                    <div style={{
                        textAlign: 'center', padding: '4rem 2rem',
                        color: '#9ca3af', fontSize: '1rem',
                    }}>
                        <i className="fa-solid fa-flag" style={{ fontSize: '2.5rem', color: '#d1d5db', display: 'block', marginBottom: '1rem' }}></i>
                        No completed BNB tournaments yet. Check back after the first one wraps up!
                    </div>
                )}

                {!loading && sortedPlayers.length > 0 && (
                    <>
                        {/* Sort tabs */}
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                            {SORT_OPTIONS.map(s => (
                                <button key={s.key} onClick={() => setSortKey(s.key)} style={{
                                    padding: '0.35rem 1rem', borderRadius: 20,
                                    border: '1.5px solid var(--bnb-navy)',
                                    background: sortKey === s.key ? 'var(--bnb-navy)' : 'transparent',
                                    color: sortKey === s.key ? '#fff' : 'var(--bnb-navy)',
                                    fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                                    transition: 'all 0.15s',
                                }}>
                                    {s.icon} {s.label}
                                </button>
                            ))}
                        </div>

                        <div className="bnb-section-label">
                            {SORT_OPTIONS.find(s => s.key === sortKey)?.icon} Sorted by {SORT_OPTIONS.find(s => s.key === sortKey)?.label}
                        </div>
                        {sortedPlayers.map((player, i) => (
                            <PlayerCard key={player.id} player={player} index={i} />
                        ))}
                    </>
                )}
            </div>
        </div>
    )
}

// ── Podium block (inside hero) ─────────────────────────────────────────────────
function PodiumBlock({ player, podiumHeight, crown = false, small = false }) {
    const ms = medalStyle(player.rank)
    const initials = player.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: small ? '0.3rem' : '0.5rem' }}>
            {crown && (
                <div style={{ fontSize: small ? '1rem' : '1.5rem', lineHeight: 1, marginBottom: '0.1rem' }}>👑</div>
            )}

            {/* Avatar with flag badge */}
            {(() => {
                const pSize  = small ? (crown ? 46 : 36) : (crown ? 100 : 80)
                const flag   = countryFlag(player.country)
                const bSize  = Math.round(pSize * 0.40)
                return (
                    <div style={{ position: 'relative', width: pSize, height: pSize }}>
                        {player.image ? (
                            <img
                                src={player.image}
                                alt={player.name}
                                style={{
                                    width: pSize, height: pSize,
                                    borderRadius: '50%', objectFit: 'cover',
                                    border: `3px solid ${ms.border}`,
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
                                    display: 'block',
                                }}
                            />
                        ) : (
                            <div style={{
                                width: pSize, height: pSize, borderRadius: '50%',
                                background: ms.bg,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: small ? (crown ? '1rem' : '0.75rem') : (crown ? '2rem' : '1.5rem'), fontWeight: 900, color: ms.text,
                                border: `3px solid ${ms.border}`,
                                boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
                            }}>
                                {initials}
                            </div>
                        )}
                        {flag && (
                            <div style={{
                                position: 'absolute', bottom: -2, right: -3,
                                width: bSize, height: bSize, borderRadius: '50%',
                                boxShadow: '0 1px 5px rgba(0,0,0,0.30)',
                                border: '1.5px solid rgba(255,255,255,0.95)',
                                zIndex: 2, overflow: 'hidden',
                                background: '#e5e7eb',
                            }}>
                                <img
                                    src={flagSvgUrl(player.country)}
                                    alt={player.country}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                />
                            </div>
                        )}
                    </div>
                )
            })()}

            {/* Name card */}
            {small ? (
                <>
                    <div style={{
                        fontSize: crown ? '0.65rem' : '0.58rem',
                        fontWeight: 800, color: ms.border, textAlign: 'center',
                        textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                        maxWidth: 60, lineHeight: 1.2,
                    }}>
                        {player.name.split(' ')[0]}
                    </div>
                    <div style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.55)', textAlign: 'center' }}>
                        {player.tournament_wins}W
                    </div>
                </>
            ) : (
                <div style={{
                    background: 'var(--bnb-navy)',
                    border: '2px solid var(--bnb-gold)',
                    borderRadius: 8,
                    padding: '0.35rem 0.9rem',
                    textAlign: 'center',
                    minWidth: 90,
                }}>
                    <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.25 }}>
                        {player.name.split(' ')[0]}
                    </div>
                    <div style={{ color: 'var(--bnb-gold)', fontSize: '0.68rem', fontWeight: 700 }}>
                        {player.tournament_wins}W
                    </div>
                </div>
            )}

            {/* Podium block */}
            <div style={{
                width: small ? (crown ? 58 : 46) : (crown ? 126 : 100),
                height: podiumHeight,
                background: ms.bg,
                borderRadius: '6px 6px 0 0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: small ? (crown ? '1.1rem' : '0.9rem') : (crown ? '2.2rem' : '1.7rem'),
                boxShadow: `0 -2px 12px ${ms.border}44`,
            }}>
                {rankLabel(player.rank)}
            </div>
        </div>
    )
}

export default LeaderboardScreen
